import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { CheckCircle, XCircle, Clock, User, Package, UserPlus, DollarSign, Mail, Phone, MapPin, Calendar, RefreshCw, Eye, Trash2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const AdminApprovals = () => {
  const { toast } = useToast();
  const { apiCall } = useAuth();
  const [pendingVendors, setPendingVendors] = useState([]);
  const [pendingListings, setPendingListings] = useState([]);
  const [pendingRevisions, setPendingRevisions] = useState([]);
  const [guestSubmissions, setGuestSubmissions] = useState([]);
  const [guestStats, setGuestStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // Bulk actions state
  const [isBulkMode, setIsBulkMode] = useState(false);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [selectedListings, setSelectedListings] = useState([]);
  const [selectedRevisions, setSelectedRevisions] = useState([]);
  const [selectedGuests, setSelectedGuests] = useState([]);
  
  // Guest submission modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  
  // T&C warning dialog state
  const [tncWarning, setTncWarning] = useState(null); // { vendor_id, vendor_name, message }

  // Auto-refresh interval (30 seconds) for real-time updates
  const POLLING_INTERVAL = 30000;

  useEffect(() => {
    fetchData();
    
    // Set up polling for real-time updates
    const pollInterval = setInterval(() => {
      fetchDataSilent(); // Silent fetch without loading spinner
    }, POLLING_INTERVAL);
    
    return () => clearInterval(pollInterval);
  }, []);

  // Silent fetch without loading state (for polling)
  const fetchDataSilent = async () => {
    try {
      const [vendors, listings, revisions, statsData, guestData] = await Promise.all([
        apiCall('/api/admin/vendors/pending'),
        apiCall('/api/admin/listings/pending'),
        apiCall('/api/admin/listings/pending-revisions'),
        apiCall('/api/admin/stats'),
        apiCall('/api/guest/submissions?status=pending_review')
      ]);
      
      setPendingVendors(vendors);
      setPendingListings(listings);
      setPendingRevisions(revisions || []);
      setStats(statsData);
      setGuestSubmissions(guestData?.submissions || []);
      setGuestStats(guestData?.status_counts || null);
      setLastUpdated(new Date());
    } catch (error) {
      // Silent fail for polling - don't show toast on every poll failure
      console.warn('Silent poll failed:', error.message);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [vendors, listings, revisions, statsData, guestData] = await Promise.all([
        apiCall('/api/admin/vendors/pending'),
        apiCall('/api/admin/listings/pending'),
        apiCall('/api/admin/listings/pending-revisions'),
        apiCall('/api/admin/stats'),
        apiCall('/api/guest/submissions?status=pending_review')
      ]);
      
      setPendingVendors(vendors);
      setPendingListings(listings);
      setPendingRevisions(revisions || []);
      setStats(statsData);
      setGuestSubmissions(guestData?.submissions || []);
      setGuestStats(guestData?.status_counts || null);
      setLastUpdated(new Date());
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load approval data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const approveVendor = async (vendorId, force = false) => {
    try {
      const url = force 
        ? `/api/admin/vendors/${vendorId}/approve?force=true`
        : `/api/admin/vendors/${vendorId}/approve`;
      const result = await apiCall(url, { method: 'PUT' });
      
      // Check if backend returned a T&C warning
      if (result?.tnc_warning) {
        setTncWarning({
          vendor_id: result.vendor_id,
          vendor_name: result.vendor_name,
          message: result.message
        });
        return;
      }
      
      toast({
        title: 'Success',
        description: 'Vendor approved successfully'
      });
      setTncWarning(null);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };
  
  const handleForceApprove = async () => {
    if (tncWarning?.vendor_id) {
      await approveVendor(tncWarning.vendor_id, true);
    }
    setTncWarning(null);
  };

  const rejectVendor = async (vendorId) => {
    try {
      await apiCall(`/api/admin/vendors/${vendorId}/reject`, { method: 'PUT' });
      toast({
        title: 'Vendor Rejected',
        description: 'Vendor has been rejected'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const approveListing = async (listingId) => {
    // Optimistic UI: immediately remove from pending list
    setPendingListings(prev => prev.filter(l => l.id !== listingId));
    
    try {
      await apiCall('/api/admin/listings/approve', {
        method: 'PUT',
        body: JSON.stringify({ listing_id: listingId, status: 'active' })
      });
      toast({
        title: 'Success',
        description: 'Listing approved and published'
      });
      // Refresh to get updated stats
      fetchDataSilent();
    } catch (error) {
      // Revert: re-add to the list if approval actually failed
      fetchData();
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const rejectListing = async (listingId) => {
    // Optimistic UI: immediately remove from pending list
    setPendingListings(prev => prev.filter(l => l.id !== listingId));
    
    try {
      await apiCall('/api/admin/listings/approve', {
        method: 'PUT',
        body: JSON.stringify({ 
          listing_id: listingId, 
          status: 'rejected',
          rejection_reason: 'Does not meet quality standards'
        })
      });
      toast({
        title: 'Listing Rejected',
        description: 'Listing has been rejected'
      });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const approveRevision = async (listingId) => {
    // Optimistic UI: immediately remove from revisions list
    setPendingRevisions(prev => prev.filter(l => l.id !== listingId));
    
    try {
      await apiCall(`/api/admin/listings/${listingId}/review-revision`, {
        method: 'POST',
        body: JSON.stringify({ action: 'approve' })
      });
      toast({
        title: 'Revision Approved',
        description: 'Listing revision has been approved and published'
      });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const rejectRevision = async (listingId) => {
    // Optimistic UI: immediately remove from revisions list
    setPendingRevisions(prev => prev.filter(l => l.id !== listingId));
    
    try {
      await apiCall(`/api/admin/listings/${listingId}/review-revision`, {
        method: 'POST',
        body: JSON.stringify({ action: 'reject', rejection_reason: 'Does not meet quality standards' })
      });
      toast({
        title: 'Revision Rejected',
        description: 'Listing revision has been rejected'
      });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Bulk action handlers
  const handleBulkApproveVendors = async () => {
    if (selectedVendors.length === 0) return;
    
    try {
      await Promise.all(
        selectedVendors.map(id => apiCall(`/api/admin/vendors/${id}/approve`, { method: 'PUT' }))
      );
      toast({
        title: 'Success',
        description: `${selectedVendors.length} vendor(s) approved successfully`
      });
      setSelectedVendors([]);
      setIsBulkMode(false);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Some approvals failed',
        variant: 'destructive'
      });
    }
  };

  const handleBulkRejectVendors = async () => {
    if (selectedVendors.length === 0) return;
    
    try {
      await Promise.all(
        selectedVendors.map(id => apiCall(`/api/admin/vendors/${id}/reject`, { method: 'PUT' }))
      );
      toast({
        title: 'Success',
        description: `${selectedVendors.length} vendor(s) rejected`
      });
      setSelectedVendors([]);
      setIsBulkMode(false);
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Some rejections failed',
        variant: 'destructive'
      });
    }
  };

  const handleBulkApproveListings = async () => {
    if (selectedListings.length === 0) return;
    
    // Optimistic UI: immediately remove selected listings
    const approvedIds = [...selectedListings];
    setPendingListings(prev => prev.filter(l => !approvedIds.includes(l.id)));
    setSelectedListings([]);
    setIsBulkMode(false);
    
    try {
      await Promise.all(
        approvedIds.map(id => 
          apiCall('/api/admin/listings/approve', {
            method: 'PUT',
            body: JSON.stringify({ listing_id: id, status: 'active' })
          })
        )
      );
      toast({
        title: 'Success',
        description: `${approvedIds.length} listing(s) approved successfully`
      });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({
        title: 'Error',
        description: 'Some approvals failed',
        variant: 'destructive'
      });
    }
  };

  const handleBulkRejectListings = async () => {
    if (selectedListings.length === 0) return;
    
    // Optimistic UI: immediately remove selected listings
    const rejectedIds = [...selectedListings];
    setPendingListings(prev => prev.filter(l => !rejectedIds.includes(l.id)));
    setSelectedListings([]);
    setIsBulkMode(false);
    
    try {
      await Promise.all(
        rejectedIds.map(id => 
          apiCall('/api/admin/listings/approve', {
            method: 'PUT',
            body: JSON.stringify({ listing_id: id, status: 'rejected' })
          })
        )
      );
      toast({
        title: 'Success',
        description: `${rejectedIds.length} listing(s) rejected`
      });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({
        title: 'Error',
        description: 'Some rejections failed',
        variant: 'destructive'
      });
    }
  };

  const handleBulkApproveRevisions = async () => {
    if (selectedListings.length === 0) return;
    const approvedIds = [...selectedListings];
    setPendingRevisions(prev => prev.filter(l => !approvedIds.includes(l.id)));
    setSelectedListings([]);
    setIsBulkMode(false);
    try {
      await Promise.all(
        approvedIds.map(id =>
          apiCall(`/api/admin/listings/${id}/review-revision`, {
            method: 'POST',
            body: JSON.stringify({ action: 'approve' })
          })
        )
      );
      toast({ title: 'Success', description: `${approvedIds.length} revision(s) approved` });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({ title: 'Error', description: 'Some revision approvals failed', variant: 'destructive' });
    }
  };

  const handleBulkRejectRevisions = async () => {
    if (selectedListings.length === 0) return;
    const rejectedIds = [...selectedListings];
    setPendingRevisions(prev => prev.filter(l => !rejectedIds.includes(l.id)));
    setSelectedListings([]);
    setIsBulkMode(false);
    try {
      await Promise.all(
        rejectedIds.map(id =>
          apiCall(`/api/admin/listings/${id}/review-revision`, {
            method: 'POST',
            body: JSON.stringify({ action: 'reject', rejection_reason: 'Does not meet quality standards' })
          })
        )
      );
      toast({ title: 'Revisions Rejected', description: `${rejectedIds.length} revision(s) rejected` });
      fetchDataSilent();
    } catch (error) {
      fetchData();
      toast({ title: 'Error', description: 'Some revision rejections failed', variant: 'destructive' });
    }
  };

  const toggleVendorSelection = (vendorId) => {
    setSelectedVendors(prev =>
      prev.includes(vendorId) ? prev.filter(id => id !== vendorId) : [...prev, vendorId]
    );
  };

  const toggleListingSelection = (listingId) => {
    setSelectedListings(prev =>
      prev.includes(listingId) ? prev.filter(id => id !== listingId) : [...prev, listingId]
    );
  };

  const selectAllVendors = () => {
    setSelectedVendors(pendingVendors.map(v => v.id));
  };

  const deselectAllVendors = () => {
    setSelectedVendors([]);
  };

  const selectAllListings = () => {
    setSelectedListings(pendingListings.map(l => l.id));
  };

  const deselectAllListings = () => {
    setSelectedListings([]);
  };

  // Guest Submission Handlers
  const approveGuestSubmission = async (submissionId) => {
    try {
      await apiCall(`/api/guest/submissions/${submissionId}/approve`, {
        method: 'POST',
        body: JSON.stringify({ admin_notes: 'Approved via admin panel' })
      });
      toast({
        title: 'Success',
        description: 'Guest submission approved and listing created'
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const openRejectModal = (submission) => {
    setSelectedSubmission(submission);
    setRejectionReason('');
    setShowRejectModal(true);
  };

  const rejectGuestSubmission = async () => {
    if (!selectedSubmission || !rejectionReason.trim()) {
      toast({
        title: 'Error',
        description: 'Please provide a rejection reason',
        variant: 'destructive'
      });
      return;
    }
    
    try {
      await apiCall(`/api/guest/submissions/${selectedSubmission.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ rejection_reason: rejectionReason })
      });
      toast({
        title: 'Submission Rejected',
        description: 'Guest submission has been rejected'
      });
      setShowRejectModal(false);
      setSelectedSubmission(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  // Bulk Guest Submission Handlers
  const handleBulkApproveGuests = async () => {
    if (selectedGuests.length === 0) return;
    try {
      await Promise.all(
        selectedGuests.map(id => apiCall(`/api/guest/submissions/${id}/approve`, {
          method: 'POST',
          body: JSON.stringify({ admin_notes: 'Bulk approved' })
        }))
      );
      toast({ title: 'Success', description: `${selectedGuests.length} submission(s) approved` });
      setSelectedGuests([]);
      setIsBulkMode(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Some approvals failed', variant: 'destructive' });
    }
  };

  const handleBulkDeleteGuests = async () => {
    if (selectedGuests.length === 0) return;
    if (!window.confirm(`Delete ${selectedGuests.length} guest submission(s)? This cannot be undone.`)) return;
    try {
      await Promise.all(
        selectedGuests.map(id => apiCall(`/api/guest/submissions/${id}`, { method: 'DELETE' }))
      );
      toast({ title: 'Success', description: `${selectedGuests.length} submission(s) deleted` });
      setSelectedGuests([]);
      setIsBulkMode(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Some deletions failed', variant: 'destructive' });
    }
  };

  // Vendor Activate (bulk)
  const handleBulkActivateVendors = async () => {
    if (selectedVendors.length === 0) return;
    try {
      await Promise.all(
        selectedVendors.map(id => apiCall(`/api/vendors/${id}/activate`, { method: 'PUT' }))
      );
      toast({ title: 'Success', description: `${selectedVendors.length} vendor(s) activated` });
      setSelectedVendors([]);
      setIsBulkMode(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: 'Some activations failed', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading approvals...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Approvals</h1>
            <p className="text-gray-600 mt-2">Manage pending vendors and listings</p>
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-sm text-gray-500">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-approvals-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Vendors</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.vendors.pending}</p>
                  </div>
                  <Clock className="h-10 w-10 text-orange-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Vendors</p>
                    <p className="text-3xl font-bold text-green-600">{stats.vendors.active}</p>
                  </div>
                  <User className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Listings</p>
                    <p className="text-3xl font-bold text-orange-600">{stats.listings.pending}</p>
                  </div>
                  <Clock className="h-10 w-10 text-orange-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Pending Revisions</p>
                    <p className="text-3xl font-bold text-blue-600">{stats.listings.pending_revisions || 0}</p>
                  </div>
                  <Clock className="h-10 w-10 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Guest Submissions</p>
                    <p className="text-3xl font-bold text-purple-600">{guestSubmissions.length}</p>
                  </div>
                  <UserPlus className="h-10 w-10 text-purple-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Active Listings</p>
                    <p className="text-3xl font-bold text-green-600">{stats.listings.active}</p>
                  </div>
                  <Package className="h-10 w-10 text-green-600" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs defaultValue="vendors" className="w-full">
          <TabsList>
            <TabsTrigger value="vendors">
              Pending Vendors ({pendingVendors.length})
            </TabsTrigger>
            <TabsTrigger value="listings">
              Pending Listings ({pendingListings.length})
            </TabsTrigger>
            <TabsTrigger value="revisions">
              Pending Revisions ({pendingRevisions.length})
            </TabsTrigger>
            <TabsTrigger value="guests">
              Guest Submissions ({guestSubmissions.length})
            </TabsTrigger>
          </TabsList>

          {/* Vendors Tab */}
          <TabsContent value="vendors">
            {pendingVendors.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500">No pending vendors</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Bulk Actions Controls */}
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                  <Button
                    variant={isBulkMode ? "default" : "outline"}
                    onClick={() => {
                      setIsBulkMode(!isBulkMode);
                      if (isBulkMode) setSelectedVendors([]);
                    }}
                  >
                    {isBulkMode ? 'Exit Bulk Mode' : 'Bulk Actions'}
                  </Button>
                  
                  {isBulkMode && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedVendors.length === pendingVendors.length && pendingVendors.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) selectAllVendors();
                            else deselectAllVendors();
                          }}
                        />
                        <span className="text-sm font-medium">
                          {selectedVendors.length} selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={selectedVendors.length === 0}
                          onClick={handleBulkApproveVendors}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={selectedVendors.length === 0}
                          onClick={handleBulkRejectVendors}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject Selected
                        </Button>
                        <Button
                          size="sm"
                          className="bg-blue-600 hover:bg-blue-700"
                          disabled={selectedVendors.length === 0}
                          onClick={handleBulkActivateVendors}
                        >
                          Activate Selected
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Vendors List */}
                {pendingVendors.map((vendor) => (
                  <Card key={vendor.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        {isBulkMode && (
                          <Checkbox
                            checked={selectedVendors.includes(vendor.id)}
                            onCheckedChange={() => toggleVendorSelection(vendor.id)}
                          />
                        )}
                        <div className="flex items-center justify-between flex-1">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold">{vendor.name}</h3>
                            <p className="text-sm text-gray-600">{vendor.email}</p>
                            <div className="flex gap-2 mt-2 flex-wrap">
                              <Badge variant="outline">{vendor.role}</Badge>
                              <Badge className="bg-orange-500">Pending</Badge>
                              {vendor.agreements?.terms_accepted ? (
                                <Badge className="bg-green-100 text-green-800 border border-green-300" data-testid={`tnc-accepted-${vendor.id}`}>
                                  <CheckCircle className="h-3 w-3 mr-1" /> T&C Accepted
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700 border border-red-300" data-testid={`tnc-not-accepted-${vendor.id}`}>
                                  <AlertTriangle className="h-3 w-3 mr-1" /> T&C Not Accepted
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Registered: {new Date(vendor.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!isBulkMode && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => approveVendor(vendor.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => rejectVendor(vendor.id)}
                                variant="destructive"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {pendingListings.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500">No pending listings</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Bulk Actions Controls */}
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                  <Button
                    variant={isBulkMode ? "default" : "outline"}
                    onClick={() => {
                      setIsBulkMode(!isBulkMode);
                      if (isBulkMode) setSelectedListings([]);
                    }}
                  >
                    {isBulkMode ? 'Exit Bulk Mode' : 'Bulk Actions'}
                  </Button>
                  
                  {isBulkMode && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedListings.length === pendingListings.length && pendingListings.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) selectAllListings();
                            else deselectAllListings();
                          }}
                        />
                        <span className="text-sm font-medium">
                          {selectedListings.length} selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={selectedListings.length === 0}
                          onClick={handleBulkApproveListings}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={selectedListings.length === 0}
                          onClick={handleBulkRejectListings}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject Selected
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Listings List */}
                {pendingListings.map((listing) => (
                  <Card 
                    key={listing.id}
                    className="cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={(e) => {
                      // Don't navigate if clicking on action buttons
                      if (e.target.closest('button') || e.target.closest('[role="checkbox"]')) return;
                      
                      // Map listing types to correct routes (plural forms)
                      const typeToRoute = {
                        'product': 'products',
                        'service': 'services',
                        'rental': 'rentals',
                        'event': 'events',
                        'classified': 'classifieds',
                        'volunteering': 'volunteering'
                      };
                      
                      const routeType = typeToRoute[listing.listing_type?.toLowerCase()] || 'products';
                      window.location.href = `/listings/${routeType}/${listing.id}`;
                    }}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        {isBulkMode && (
                          <Checkbox
                            checked={selectedListings.includes(listing.id)}
                            onCheckedChange={() => toggleListingSelection(listing.id)}
                          />
                        )}
                        <div className="flex items-center justify-between flex-1">
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold">{listing.title}</h3>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {listing.description}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">{listing.listing_type}</Badge>
                              <Badge variant="secondary">{listing.category}</Badge>
                              <Badge className="bg-orange-500">Pending Approval</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Vendor: {listing.vendor_name} • Submitted: {new Date(listing.created_at).toLocaleDateString()}
                            </p>
                            <p className="text-xs text-blue-600 mt-1 hover:underline">
                              Click to view details →
                            </p>
                          </div>
                          {!isBulkMode && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => approveListing(listing.id)}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => rejectListing(listing.id)}
                                variant="destructive"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Revisions Tab */}
          <TabsContent value="revisions">
            {pendingRevisions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-gray-500">No pending revisions</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Bulk Actions Controls */}
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                  <Button
                    variant={isBulkMode ? "default" : "outline"}
                    onClick={() => {
                      setIsBulkMode(!isBulkMode);
                      if (isBulkMode) setSelectedListings([]);
                    }}
                  >
                    {isBulkMode ? 'Exit Bulk Mode' : 'Bulk Actions'}
                  </Button>
                  
                  {isBulkMode && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedListings.length === pendingRevisions.length && pendingRevisions.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedListings(pendingRevisions.map(l => l.id));
                            else setSelectedListings([]);
                          }}
                        />
                        <span className="text-sm font-medium">
                          {selectedListings.length} selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={selectedListings.length === 0}
                          onClick={handleBulkApproveRevisions}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve Selected
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={selectedListings.length === 0}
                          onClick={handleBulkRejectRevisions}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Reject Selected
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {pendingRevisions.map((listing) => (
                  <Card key={listing.id}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        {isBulkMode && (
                          <Checkbox
                            checked={selectedListings.includes(listing.id)}
                            onCheckedChange={() => toggleListingSelection(listing.id)}
                          />
                        )}
                        <div className="flex items-center justify-between flex-1">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <Badge className="bg-blue-600">Revision for ACTIVE Listing</Badge>
                            </div>
                            <h3 className="text-lg font-semibold">{listing.title}</h3>
                            <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                              {listing.description}
                            </p>
                            <div className="flex gap-2 mt-2">
                              <Badge variant="outline">{listing.listing_type}</Badge>
                              <Badge variant="secondary">{listing.category}</Badge>
                              <Badge className="bg-orange-500">Pending Revision Approval</Badge>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Vendor: {listing.vendor_name} • Updated: {new Date(listing.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                          {!isBulkMode && (
                            <div className="flex gap-2">
                              <Button
                                onClick={() => approveRevision(listing.id)}
                                className="bg-green-600 hover:bg-green-700"
                                size="sm"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => rejectRevision(listing.id)}
                                variant="destructive"
                                size="sm"
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                              <Button
                                onClick={() => window.location.href = `/admin/revisions/${listing.id}`}
                                variant="outline"
                                size="sm"
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                View Diff
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Guest Submissions Tab */}
          <TabsContent value="guests">
            {guestSubmissions.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No pending guest submissions</p>
                  {guestStats && (
                    <p className="text-sm text-gray-400 mt-2">
                      {guestStats.pending_payment || 0} awaiting payment • {guestStats.live || 0} live
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Stats Summary */}
                {guestStats && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-yellow-700">Pending Payment</p>
                      <p className="text-lg font-bold text-yellow-800">{guestStats.pending_payment || 0}</p>
                    </div>
                    <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-orange-700">Pending Review</p>
                      <p className="text-lg font-bold text-orange-800">{guestStats.pending_review || 0}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-green-700">Approved</p>
                      <p className="text-lg font-bold text-green-800">{guestStats.approved || 0}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-blue-700">Live</p>
                      <p className="text-lg font-bold text-blue-800">{guestStats.live || 0}</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-xs text-red-700">Rejected</p>
                      <p className="text-lg font-bold text-red-800">{guestStats.rejected || 0}</p>
                    </div>
                  </div>
                )}

                {/* Guest Submissions List */}
                {/* Bulk Actions Controls */}
                <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border">
                  <Button
                    variant={isBulkMode ? "default" : "outline"}
                    onClick={() => {
                      setIsBulkMode(!isBulkMode);
                      if (isBulkMode) setSelectedGuests([]);
                    }}
                  >
                    {isBulkMode ? 'Exit Bulk Mode' : 'Bulk Actions'}
                  </Button>
                  
                  {isBulkMode && (
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedGuests.length === guestSubmissions.length && guestSubmissions.length > 0}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedGuests(guestSubmissions.map(s => s.id));
                            else setSelectedGuests([]);
                          }}
                        />
                        <span className="text-sm font-medium">
                          {selectedGuests.length} selected
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="bg-green-600 hover:bg-green-700"
                          disabled={selectedGuests.length === 0}
                          onClick={handleBulkApproveGuests}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={selectedGuests.length === 0}
                          onClick={handleBulkDeleteGuests}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                {guestSubmissions.map((submission) => (
                  <Card key={submission.id} className="overflow-hidden">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row md:items-start gap-4">
                        {isBulkMode && (
                          <Checkbox
                            checked={selectedGuests.includes(submission.id)}
                            onCheckedChange={() => {
                              setSelectedGuests(prev =>
                                prev.includes(submission.id)
                                  ? prev.filter(id => id !== submission.id)
                                  : [...prev, submission.id]
                              );
                            }}
                            className="mt-1"
                          />
                        )}
                        {/* Submission Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={submission.submission_type === 'classified' ? 'bg-purple-600' : 'bg-teal-600'}>
                              {submission.submission_type === 'classified' ? 'Classified' : 'Volunteering'}
                            </Badge>
                            <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300">
                              {submission.status === 'pending_review' ? 'Pending Review' : submission.status}
                            </Badge>
                            {submission.listing_fee > 0 && (
                              <Badge variant="outline" className={submission.listing_fee_paid ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}>
                                <DollarSign className="h-3 w-3 mr-1" />
                                {submission.listing_fee_paid ? 'Paid' : `₹${submission.listing_fee} Pending`}
                              </Badge>
                            )}
                          </div>
                          
                          <h3 className="text-lg font-semibold">{submission.title}</h3>
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                            {submission.description}
                          </p>
                          
                          <div className="flex flex-wrap gap-2 mt-3">
                            {submission.category_name && (
                              <Badge variant="secondary">{submission.category_name}</Badge>
                            )}
                            {submission.price && (
                              <Badge variant="outline">₹{submission.price.toLocaleString()}</Badge>
                            )}
                            {submission.organization_name && (
                              <Badge variant="outline">{submission.organization_name}</Badge>
                            )}
                          </div>
                          
                          {/* Guest Contact Info */}
                          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs font-medium text-gray-500 mb-2">Guest Contact</p>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                              <div className="flex items-center gap-1 text-gray-700">
                                <User className="h-3 w-3 text-gray-400" />
                                {submission.guest_name}
                              </div>
                              <div className="flex items-center gap-1 text-gray-700">
                                <Mail className="h-3 w-3 text-gray-400" />
                                {submission.guest_email}
                              </div>
                              <div className="flex items-center gap-1 text-gray-700">
                                <Phone className="h-3 w-3 text-gray-400" />
                                {submission.guest_phone}
                              </div>
                              {submission.location && (
                                <div className="flex items-center gap-1 text-gray-700 md:col-span-2">
                                  <MapPin className="h-3 w-3 text-gray-400" />
                                  {submission.location}
                                </div>
                              )}
                              {submission.guest_city && (
                                <div className="flex items-center gap-1 text-gray-700">
                                  <MapPin className="h-3 w-3 text-gray-400" />
                                  {submission.guest_city}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {/* Volunteering specific info */}
                          {submission.submission_type === 'volunteering' && (
                            <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-500">
                              {submission.start_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="h-3 w-3" />
                                  {submission.start_date} {submission.end_date && `- ${submission.end_date}`}
                                </span>
                              )}
                              {submission.commitment_type && (
                                <span className="capitalize">• {submission.commitment_type}</span>
                              )}
                            </div>
                          )}
                          
                          <p className="text-xs text-gray-500 mt-3">
                            Submitted: {new Date(submission.created_at).toLocaleDateString()} at {new Date(submission.created_at).toLocaleTimeString()}
                          </p>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex md:flex-col gap-2 shrink-0">
                          <Button
                            onClick={() => approveGuestSubmission(submission.id)}
                            className="bg-green-600 hover:bg-green-700 flex-1 md:flex-none"
                            disabled={submission.status !== 'pending_review'}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Approve
                          </Button>
                          <Button
                            onClick={() => openRejectModal(submission)}
                            variant="destructive"
                            className="flex-1 md:flex-none"
                            disabled={submission.status !== 'pending_review'}
                          >
                            <XCircle className="h-4 w-4 mr-2" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Rejection Reason Modal */}
        <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reject Guest Submission</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-gray-600">
                Please provide a reason for rejecting this submission. This will be sent to the guest.
              </p>
              {selectedSubmission && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium text-sm">{selectedSubmission.title}</p>
                  <p className="text-xs text-gray-500">by {selectedSubmission.guest_name}</p>
                </div>
              )}
              <Textarea
                placeholder="Enter rejection reason..."
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRejectModal(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={rejectGuestSubmission}>
                Reject Submission
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* T&C Warning Alert Dialog */}
        <AlertDialog open={!!tncWarning} onOpenChange={(open) => !open && setTncWarning(null)}>
          <AlertDialogContent data-testid="tnc-warning-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
                <ShieldAlert className="h-5 w-5" />
                Seller Agreement Not Accepted
              </AlertDialogTitle>
              <AlertDialogDescription className="text-sm text-gray-700">
                <strong>{tncWarning?.vendor_name}</strong> has not yet accepted the Seller Agreement & Terms of Service.
                <br /><br />
                Approving this vendor without their explicit acceptance may have legal implications. Are you sure you want to proceed?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="tnc-warning-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleForceApprove}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="tnc-warning-force-approve"
              >
                Approve Anyway
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
};

export default AdminApprovals;
